import { EventEmitter } from '@spotify-internal/emitter';
import { StatusCode } from './enums/status_code';
import { Event } from './enums/event';
const DEFAULT_PRODUCT_STATE_ENDPOINT = '@webgate/melody';
const USER_UPDATE_EVENT_FILTER = /^wss:\/\/event$/;
export class ProductStateObserver extends EventEmitter {
    constructor(options) {
        super();
        this._currentState = null;
        this._connectionId = null;
        this._transport = options.transport;
        this._endpoint = options.endpoint || DEFAULT_PRODUCT_STATE_ENDPOINT;
        this._handleUserUpdate = this._handleUserUpdate.bind(this);
        this._init();
    }
    static create(options) {
        return new ProductStateObserver(options);
    }
    _init() {
        this._transport.on(Event.CONNECTION_ID, (e) => {
            this.register(e.data.id).catch(function () { });
        });
    }
    _handleUserUpdate() {
        // When we get a product state change, we should refresh the user's token.
        this._transport.forceTokenRefresh().catch(function () {
            // NO OP
        });
        this.getCurrentState(true)
            .then((productState) => {
            this.emit(Event.PRODUCT_STATE_CHANGED, { productState });
        })
            .catch(function () {
            // ignore
        });
    }
    _shouldRetry(response, _StatusFamily) {
        const statusFamily = response.getStatusFamily();
        return (statusFamily === _StatusFamily.SERVER_ERROR ||
            statusFamily === _StatusFamily.CONNECTION_ERROR);
    }
    _createSubURL(connectionId) {
        return `@webapi/v1/me/notifications/user?connection_id=${encodeURIComponent(connectionId)}`;
    }
    getCurrentState(ignoreCached) {
        if (!ignoreCached && this._currentState) {
            return Promise.resolve(this._currentState);
        }
        return this._transport
            .request(`${this._endpoint}/v1/product_state`, {
            method: 'GET',
            responseType: 'json',
            retry: {
                condition: this._shouldRetry,
            },
        })
            .then((response) => {
            if (response.status !== StatusCode.OK || !response.body) {
                this._currentState = null;
                throw new Error("Cannot get the user's product state.");
            }
            this._currentState = response.body;
            return response.body;
        });
    }
    register(connectionId) {
        if (!connectionId) {
            Promise.reject(new TypeError('ConnectionId cannot be null'));
        }
        this._transport.matchMessages(USER_UPDATE_EVENT_FILTER, this._handleUserUpdate);
        return this._transport
            .request(this._createSubURL(connectionId), {
            method: 'PUT',
            ignoreResponseBody: true,
            ignoreUnauthorized: true,
            retry: {
                condition: this._shouldRetry,
            },
        })
            .then((response) => {
            if (response.status !== StatusCode.OK) {
                const errorMessage = `Cannot register for product state updates: service responded with status ${response.status}`;
                throw new Error(errorMessage);
            }
            this._connectionId = connectionId;
            return true;
        });
    }
    deregister() {
        const connectionId = this._connectionId;
        if (!connectionId) {
            return Promise.resolve(false);
        }
        this._transport.unmatchMessages(USER_UPDATE_EVENT_FILTER, this._handleUserUpdate);
        return this._transport
            .request(this._createSubURL(connectionId), {
            method: 'DELETE',
            ignoreResponseBody: true,
            ignoreUnauthorized: true,
            retry: {
                condition: this._shouldRetry,
            },
        })
            .then((response) => {
            if (response.status === StatusCode.OK) {
                this._connectionId = null;
                return true;
            }
            return false;
        });
    }
}
//# sourceMappingURL=product_state_observer.js.map